import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from "react";
import { CODESMITH_EXTENSION_EMBED_VALUE } from "@/lib/codesmithExtensionEmbed";

/**
 * Where the dashboard is running: a browser tab, the Chrome extension's side
 * panel iframe (`?embed=extension`), or the macOS desktop shell.
 */
export type CodesmithSurface = "web" | "extension" | "desktop";

/** Preload bridge the macOS desktop shell exposes as `window.codesmithDesktop`. */
export interface CodesmithDesktopBridge {
  version: 1;
  appVersion: string;
  openExternal(url: string): void;
  startLogin(): void;
  setBadge(count: number): void;
}
declare global {
  interface Window {
    codesmithDesktop?: CodesmithDesktopBridge;
  }
}
type EmbedParam = string | string[] | null | undefined;
export function codesmithDesktopBridge(): CodesmithDesktopBridge | null {
  if (typeof window === "undefined") return null;
  return window.codesmithDesktop ?? null;
}
function surfaceFor(embed: EmbedParam, desktop: boolean): CodesmithSurface {
  if (desktop) return "desktop";
  const extension = Array.isArray(embed) ? embed.includes(CODESMITH_EXTENSION_EMBED_VALUE) : embed === CODESMITH_EXTENSION_EMBED_VALUE;
  return extension ? "extension" : "web";
}

/**
 * For event handlers and effects. `embed` is the page's `embed` query value;
 * the desktop bridge wins over it.
 */
export function codesmithSurface(embed: EmbedParam): CodesmithSurface {
  return surfaceFor(embed, codesmithDesktopBridge() !== null);
}
const subscribeToNothing = () => () => {};
const hasDesktopBridge = () => codesmithDesktopBridge() !== null;
const serverHasDesktopBridge = () => false;

/**
 * For render. The bridge is injected before any page script runs and never
 * changes, but the server cannot see it, so hydration reads `false` and the
 * first client render after it picks up the bridge.
 */
export function useCodesmithSurface(embed) {
  const $ = _c(3);
  const desktop = useSyncExternalStore(subscribeToNothing, hasDesktopBridge, serverHasDesktopBridge);
  let t0;
  if ($[0] !== desktop || $[1] !== embed) {
    t0 = surfaceFor(embed, desktop);
    $[0] = desktop;
    $[1] = embed;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}